import { Warning as WarningIcon } from '@mui/icons-material';
import { Button, Chip, ChipProps, Link, Stack, Box } from '@mui/material';
import { AxiosError } from 'axios';
import { isBoolean, isNil, isNumber } from 'lodash';
import React, { Fragment, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';

import { orderRequisitesApi, ordersApi } from 'api';
import {
  BankLabel,
  CallbackUrlStatusLabel,
  CloseDialogResult,
  CloseFormikDialogResult,
  ConfirmButton,
  ContactInfo,
  CopyText,
  CounterpartyFlowChip,
  DataWrapper,
  DateLabel,
  DescriptionRow,
  Dialog,
  Money,
  OrderAgentCashback,
  OrderReceiptsValidation,
  OrderStatusDetailsInfo,
  OrderStatusLabel,
  PaymentTypeLabel,
  RequisitesInfo,
  StyledBankLabel,
  StylizedNumber,
  VerificationOrderStatusLabel,
} from 'components';
import { ORDER_WITH_RECEIPTS_STATUSES } from 'constants/order.constants';
import {
  CustomerCancelReason,
  DefaultPaymentTypeCode,
  OrderStatus,
  OrderStatusDetails,
  OrderType,
  QueryKey,
  WebhookMethod,
} from 'enums';
import { AutomationStatus, DisputeDialog } from 'features/orders';
import { useMeta, useUser, useUserContextUtils } from 'hooks';
import { TranslationNamespace } from 'i18n';
import {
  ChangeDisputeAmountDto,
  PayinOrder,
  PayinOrderDispute,
  ResolvePayinOrderDispute,
  UpdateInternalStatusDetailsDto,
} from 'types';
import { formatUtils, orderUtils } from 'utils';

import { ChangeDisputeAmountDialog } from '../ChangeDisputeAmountDialog';
import { InternalStatusDetailsDialog } from '../InternalStatusDetailsDialog';
import { MerchantRequests } from '../MerchantRequests';
import { OrderStatusHistory } from '../OrderStatusHistory';
import { OrderWebhooksLink } from '../OrderWebhooksLink';
import { P2PProviderOrderDetails } from '../P2PProviderOrderDetails';
import { P2PProvidersRequests } from '../P2PProvidersRequests';
import { ReceiptsModal } from '../ReceiptsModal';
import { ResolveDisputeDialog } from '../ResolveDisputeDialog';
import { SciEvents } from '../SciEvents';

type MoneyChipProps = {
  value: number;
  fiatCurrencyId: string;
  color: ChipProps['color'];
};

const MoneyChip: React.FC<MoneyChipProps> = ({ color, ...rest }) => (
  <Chip
    size="small"
    color={color}
    variant="outlined"
    sx={{ alignSelf: 'flex-start' }}
    label={<Money {...rest} symbol />}
  />
);

type Props = {
  order: PayinOrder;
  loading: boolean;
  resolveOrderDisputeAsAdminError?: AxiosError<any> | null;
  changeDisputeAmountError?: AxiosError<any> | null;
  onConfirm: () => void;
  onCancelCompleted: () => void;
  onCompleteCancelled: () => void;
  onOpenDispute: (dispute: PayinOrderDispute) => void;
  onResolveDispute: (resolveDispute: ResolvePayinOrderDispute) => void;
  onChangeDisputeAmount: (data: ChangeDisputeAmountDto) => void;
  onUpdateInternalStatusDetails: (
    details: UpdateInternalStatusDetailsDto,
  ) => void;
};

export const OrderDetails: React.FC<Props> = ({
  order,
  loading,
  resolveOrderDisputeAsAdminError,
  changeDisputeAmountError,
  onCancelCompleted,
  onCompleteCancelled,
  onConfirm,
  onOpenDispute,
  onResolveDispute,
  onChangeDisputeAmount,
  onUpdateInternalStatusDetails,
}: Props) => {
  const { t } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'features.orders.table.columns',
  });
  const { t: tOrders } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'features.orders.details',
  });
  const { t: tTooltips } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'features.orders.table.tooltips',
  });
  const { t: tCommon } = useTranslation(TranslationNamespace.Common);
  const { getPaymentTypeCode } = useUserContextUtils();

  const { traderCanOpenDispute, traderCanCompleteCancelled } = useMeta();

  const [receiptsModalOpen, setReceiptsModalOpen] = useState(false);
  const [disputeDialogOpen, setDisputeDialogOpen] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const { isMerchant, isTrader, isManager, isAdminOrTechOperator, role } =
    useUser();
  const [resolveDisputeDialogOpen, setResolveDisputeDialogOpen] =
    useState(false);
  const [internalStatusDetailsDialogOpen, setInternalStatusDetailsDialogOpen] =
    useState(false);
  const [changeDisputeAmountDialogOpen, setChangeDisputeAmountDialogOpen] =
    useState(false);

  const isTraderOrCustomerConfirmStatus = useMemo(
    () =>
      order?.status === OrderStatus.TraderConfirm ||
      order?.status === OrderStatus.CustomerConfirm,
    [order],
  );

  const hasReceipt = useMemo(
    () => ORDER_WITH_RECEIPTS_STATUSES.includes(order?.status),
    [order],
  );

  const isRequisitesVerification = useMemo(
    () => order.requisitesVerification,
    [order],
  );

  const isCompleted = useMemo(
    () => order?.status === OrderStatus.Completed,
    [order],
  );

  const isHoldCompleted = useMemo(
    () => order?.status === OrderStatus.HoldCompleted,
    [order],
  );

  const isDispute = useMemo(
    () => order?.status === OrderStatus.Dispute,
    [order],
  );

  const isDifferentAmountDispute = useMemo(
    () =>
      isAdminOrTechOperator &&
      order?.status === OrderStatus.Dispute &&
      order?.statusDetails === OrderStatusDetails.DisputeDifferentAmount &&
      !!order?.p2pProviderOrderId,
    [isAdminOrTechOperator, order],
  );

  const isCancelled = useMemo(
    () => order?.status === OrderStatus.Cancelled,
    [order],
  );

  const isCrossFiatPayment = useMemo(
    () => orderUtils.isCrossFiatPayment(order),
    [order],
  );

  const isNSPK = useMemo(
    () =>
      getPaymentTypeCode({ id: order?.paymentTypeId }) ===
      DefaultPaymentTypeCode.NSPK,
    [order, getPaymentTypeCode],
  );

  const shouldShowRandomizedAmount = useMemo(
    () =>
      order.initialAmount &&
      order.initialAmount !== order.amount &&
      order.previousAmount &&
      order.initialAmount !== order.previousAmount,
    [order.initialAmount, order.amount, order.previousAmount],
  );

  const handleOpenDispute = useCallback(() => {
    setDisputeDialogOpen(true);
  }, []);

  const handleConfirmClick = useCallback(() => {
    setConfirmDialogOpen(true);
  }, []);

  const handleReceiptsClick = useCallback(() => {
    setReceiptsModalOpen(true);
  }, []);

  const handleReceiptsModalClose = useCallback(() => {
    setReceiptsModalOpen(false);
  }, []);

  const handleDisputeDialogClose = useCallback(
    ({ ok, data }: CloseDialogResult<PayinOrderDispute>) => {
      if (ok && data) {
        onOpenDispute?.(data);
      }
      setDisputeDialogOpen(false);
    },
    [onOpenDispute],
  );

  const handleConfirmDialogClose = useCallback(
    async ({ ok }: CloseDialogResult<PayinOrderDispute>) => {
      if (ok) {
        await onConfirm?.();
      }
      setConfirmDialogOpen(false);
    },
    [onConfirm],
  );

  const statusHistoryQueryKey = useMemo(
    () => [QueryKey.OrderStatusHistory, order?.id],
    [order],
  );

  const statusHistoryQueryResult = useQuery(
    statusHistoryQueryKey,
    () => ordersApi.getOrderStatusHistoryAsRole(role)(order.id),
    { enabled: !!order?.id && (isManager || isTrader) },
  );

  const { data: nspkDetails } = useQuery(
    QueryKey.NspkDetails,
    () => orderRequisitesApi.getNspkDetails(order?.id || ''),
    { enabled: isNSPK && order?.status === OrderStatus.CustomerConfirm },
  );

  const handleResolveDisputeDialogClose = useCallback(
    ({ ok, data }: CloseFormikDialogResult<ResolvePayinOrderDispute>) => {
      if (ok && data) {
        onResolveDispute?.(data.values);
      }
      setResolveDisputeDialogOpen(false);
    },
    [onResolveDispute],
  );

  const handleInternalStatusDetailsDialogClose = useCallback(
    (result: CloseFormikDialogResult<UpdateInternalStatusDetailsDto>) => {
      if (result.ok && result.data) {
        onUpdateInternalStatusDetails(result.data.values);
      }
      setInternalStatusDetailsDialogOpen(false); // todo: close on success ?
    },
    [onUpdateInternalStatusDetails],
  );

  const handleChangeDisputeAmountDialogClose = useCallback(
    ({ ok, data }: CloseFormikDialogResult<ChangeDisputeAmountDto>) => {
      if (ok && data) {
        onChangeDisputeAmount(data.values);
      }
      setChangeDisputeAmountDialogOpen(false);
    },
    [onChangeDisputeAmount],
  );

  const paymentTypeIdWithFallback = useMemo(() => {
    if (
      isNSPK &&
      isTrader &&
      order.paymentTypeId !== order.requisites?.paymentTypeId
    ) {
      return order.requisites?.paymentTypeId;
    }
    return order.paymentTypeId;
  }, [isTrader, isNSPK, order]);

  return (
    <Fragment>
      <DataWrapper isLoading={loading}>
        <Stack direction="column" spacing={6}>
          <Stack direction="column" spacing={3}>
            <DescriptionRow title={t('id')} value={order.id} canCopy />
            <DescriptionRow
              title={t('status')}
              value={
                <Fragment>
                  {isRequisitesVerification ? (
                    <VerificationOrderStatusLabel order={order} compact />
                  ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <OrderStatusLabel status={order?.status} />
                      {!isMerchant && (
                        <Box ml={2}>
                          <AutomationStatus order={order} />
                        </Box>
                      )}
                    </Box>
                  )}
                  <OrderStatusDetailsInfo order={order} />
                </Fragment>
              }
            />
            <DescriptionRow
              title={t('amount')}
              description={tTooltips('amount')}
              value={
                <Fragment>
                  <MoneyChip
                    value={order.amount}
                    fiatCurrencyId={order.fiatCurrencyId}
                    color="success"
                  />
                  {isCrossFiatPayment && (
                    <div>
                      {'~'}
                      <Money
                        value={order.crossFiatCurrencyAmount}
                        fiatCurrencyId={order.crossFiatCurrencyId}
                        symbol
                      />
                      {' ('}
                      <Money
                        value={order.crossFiatCurrencyRate}
                        fiatCurrencyId={order.fiatCurrencyId}
                        symbol
                      />
                      {')'}
                    </div>
                  )}
                  <div>
                    {isManager && order.warnings?.currencyRate && (
                      <WarningIcon className="tw-mr-1" color="warning" />
                    )}
                    <Money
                      value={order.assetCurrencyAmount}
                      assetCurrencyId={order.assetCurrencyId}
                      symbol
                    />
                    {' ('}
                    <Money
                      value={order.currencyRate}
                      fiatCurrencyId={order.fiatCurrencyId}
                      symbol
                    />
                    {')'}
                  </div>
                </Fragment>
              }
            />
            {order.initialAmount && (
              <DescriptionRow
                title={t('initial_amount')}
                description={tTooltips('initial_amount')}
                value={
                  <Money
                    value={order.initialAmount}
                    fiatCurrencyId={order.fiatCurrencyId}
                    symbol
                  />
                }
              />
            )}
            {shouldShowRandomizedAmount && (
              <DescriptionRow
                title={t('randomized_amount')}
                description={tTooltips('randomized_amount')}
                value={
                  <Money
                    value={order.previousAmount!}
                    fiatCurrencyId={order.fiatCurrencyId}
                    symbol
                  />
                }
              />
            )}
            {!!order.disputeAmount && (
              <DescriptionRow
                title={t('dispute_amount')}
                description={
                  !!order.p2pProviderOrderId
                    ? tTooltips('provider_dispute_amount')
                    : ''
                }
                value={
                  <MoneyChip
                    value={order.disputeAmount}
                    fiatCurrencyId={order.fiatCurrencyId}
                    color="warning"
                  />
                }
              />
            )}
            {(isManager || isMerchant) &&
              isCrossFiatPayment &&
              !isNil(order.spreadPercentage) && (
                <DescriptionRow
                  title={t('spread')}
                  value={
                    <StylizedNumber value={order.spreadPercentage} unit="%" />
                  }
                />
              )}
            {isManager && (
              <Fragment>
                <DescriptionRow
                  title={t('customer')}
                  value={
                    <Fragment>
                      {order.customerId && <CopyText text={order.customerId} />}
                      <div>{order.customerName}</div>
                      <ContactInfo
                        phone={order.customerPhone}
                        email={order.customerEmail}
                      />
                      {isBoolean(order.isCounterpartyFlow) && (
                        <CounterpartyFlowChip
                          isCounterpartyFlow={order.isCounterpartyFlow}
                          className="tw-mt-1"
                        />
                      )}
                    </Fragment>
                  }
                />
                <DescriptionRow
                  title={t('shop')}
                  value={
                    <Fragment>
                      <CopyText text={order.shopId} />
                      <div>{formatUtils.formatName(order.shop)}</div>
                    </Fragment>
                  }
                />
                <DescriptionRow
                  title={t('merchant')}
                  value={
                    <Fragment>
                      <CopyText text={order.merchantId} />
                      <div>{order.merchant?.user?.name}</div>
                    </Fragment>
                  }
                />
              </Fragment>
            )}
            {(isManager || isMerchant) && (
              <Fragment>
                {order.externalOrderId && (
                  <DescriptionRow
                    title={t('external_order_id')}
                    value={
                      <Fragment>
                        <CopyText text={order.externalOrderId} />
                      </Fragment>
                    }
                  />
                )}
                {order.callbackUrl && (
                  <DescriptionRow
                    title={t('callback')}
                    value={
                      <Fragment>
                        <div className="tw-flex">
                          <CopyText
                            className="tw-break-all"
                            text={order.callbackUrl}
                            label={`[${
                              order.callbackMethod || WebhookMethod.Post
                            }] ${order.callbackUrl}`}
                          />
                        </div>
                        {order.callbackUrlStatus && (
                          <div>
                            <CallbackUrlStatusLabel
                              status={order.callbackUrlStatus}
                            />
                          </div>
                        )}
                      </Fragment>
                    }
                  />
                )}
              </Fragment>
            )}
            {isMerchant && (
              <DescriptionRow
                title={t('accrual')}
                value={
                  <Money
                    value={order.shopAmount}
                    assetCurrencyId={order.assetCurrencyId}
                    symbol
                  />
                }
              />
            )}
            {!isMerchant && !!order.traderCompensation && (
              <DescriptionRow
                title={t('trader_compensation')}
                value={
                  <Money
                    value={order.traderCompensation}
                    assetCurrencyId={order.assetCurrencyId}
                    symbol
                  />
                }
              />
            )}
            {(isManager || isTrader) && isCrossFiatPayment && (
              <DescriptionRow
                title={t('trader_spread_compensation')}
                value={
                  <Money
                    value={order.traderSpreadCompensation}
                    assetCurrencyId={order.assetCurrencyId}
                    symbol
                  />
                }
              />
            )}
            {isManager && (
              <Fragment>
                {order?.shopFee && (
                  <DescriptionRow
                    title={t('shop_fee')}
                    value={
                      <Fragment>
                        <Money
                          value={order.shopFee}
                          assetCurrencyId={order.assetCurrencyId}
                          symbol
                        />
                        {isNumber(order?.initialShopCommission) &&
                          ` (${order.initialShopCommission}%)`}
                      </Fragment>
                    }
                  />
                )}
                <OrderAgentCashback order={order} operation={OrderType.Payin} />
                {isNumber(order.expectedProviderFeeAmount) && (
                  <DescriptionRow
                    title={t('p2p_provider_fee')}
                    value={
                      <div>
                        <div
                          className="tw-flex"
                          key={t('expected_provider_fee_amount')}
                        >
                          <div className="tw-mr-2">
                            {t('expected_provider_fee_amount')}
                          </div>
                          <Money
                            value={order.expectedProviderFeeAmount}
                            assetCurrencyId={order.assetCurrencyId}
                            symbol
                          />
                          {isNumber(order?.initialProviderCompensation) &&
                            ` (${order.initialProviderCompensation}%)`}
                        </div>
                        <div
                          className="tw-flex"
                          key={t('actual_provider_fee_amount')}
                        >
                          <div className="tw-mr-2">
                            {t('actual_provider_fee_amount')}
                          </div>
                          {order.warnings?.providerFeeAmount && (
                            <WarningIcon className="tw-mr-1" color="warning" />
                          )}
                          <Money
                            value={order.actualProviderFeeAmount || 0}
                            assetCurrencyId={order.assetCurrencyId}
                            symbol
                          />
                          {isNumber(order?.initialProviderCompensation) &&
                            ` (${order.initialProviderCompensation}%)`}
                        </div>
                      </div>
                    }
                  />
                )}
              </Fragment>
            )}
            {isManager && paymentTypeIdWithFallback && (
              <DescriptionRow
                title={t('payment_type')}
                value={
                  <Fragment>
                    <PaymentTypeLabel
                      paymentTypeId={paymentTypeIdWithFallback}
                    />
                    {order.bankId && <StyledBankLabel bankId={order.bankId} />}
                  </Fragment>
                }
              />
            )}
            {order.traderId && order.requisites && (
              <DescriptionRow
                title={t('trader_requisites')}
                value={
                  <RequisitesInfo
                    cardholder={order.requisites.cardholder}
                    bankId={order.requisites.bankId || order.bankId}
                    paymentTypeId={
                      order.requisites.paymentTypeId || order.paymentTypeId
                    }
                    cardInfo={order.requisites.cardInfo}
                    phone={order.requisites.phone}
                    swiftBic={order.requisites.swiftBic}
                    bic={order.requisites.bic}
                    email={order.requisites.email}
                    idCard={order.requisites.idCard}
                    beneficiaryName={order.requisites.beneficiaryName}
                    accountNumber={order.requisites.accountNumber}
                    name={order.requisites.name}
                    expirationDate={order.requisites.expirationDate}
                    taxId={order.requisites.taxId}
                    paymentLink={order.requisites.paymentLink}
                    accountNumberQrCodeGenerated={
                      order.requisites.accountNumberQrCodeGenerated
                    }
                  />
                }
              />
            )}
            {(order.paymentCustomerCardFirstDigits ||
              order.paymentCustomerCardLastDigits) && (
              <DescriptionRow
                title={t('customer_requisites')}
                value={
                  <div>
                    {formatUtils.formatCardDigits({
                      firstDigits: order.paymentCustomerCardFirstDigits,
                      lastDigits: order.paymentCustomerCardLastDigits,
                    })}
                  </div>
                }
              />
            )}
            {order.paymentCustomerPhoneLastDigits && (
              <DescriptionRow
                title={t('customer_phone')}
                value={formatUtils.formatPhoneLastDigits(
                  order.paymentCustomerPhoneLastDigits,
                )}
              />
            )}
            {order.paymentCustomerBank && (
              <DescriptionRow
                title={t('customer_bank')}
                value={<BankLabel bankCode={order.paymentCustomerBank} />}
              />
            )}
            {order.paymentCustomerName && (
              <DescriptionRow
                title={t('customer_name')}
                value={order.paymentCustomerName}
              />
            )}
            {order.paymentCustomerIBAN && (
              <DescriptionRow
                title={t('customer_iban')}
                value={order.paymentCustomerIBAN}
              />
            )}
            {order.paymentCustomerAccountNumber && (
              <DescriptionRow
                title={t('customer_account_number')}
                value={order.paymentCustomerAccountNumber}
              />
            )}
            {order.paymentCustomerUtr && (
              <DescriptionRow
                title={t('utr')}
                value={order.paymentCustomerUtr}
              />
            )}
            {orderUtils.isCustomerPaid(
              order,
              statusHistoryQueryResult.data,
            ) && (
              <DescriptionRow
                title={t('payment_date')}
                value={
                  <Fragment>
                    <DateLabel>
                      {formatUtils.formatDate(
                        orderUtils.getStatusDate(
                          order,
                          OrderStatus.TraderConfirm,
                          statusHistoryQueryResult.data,
                        ),
                      )}
                    </DateLabel>
                    {(order.paymentCustomerCardFirstDigits ||
                      order.paymentCustomerCardLastDigits) && (
                      <div>
                        {formatUtils.formatCardDigits({
                          firstDigits: order.paymentCustomerCardFirstDigits,
                          lastDigits: order.paymentCustomerCardLastDigits,
                        })}
                      </div>
                    )}
                    {order.paymentCustomerPhoneLastDigits && (
                      <div>
                        {formatUtils.formatPhoneLastDigits(
                          order.paymentCustomerPhoneLastDigits,
                        )}
                      </div>
                    )}
                    {paymentTypeIdWithFallback && (
                      <PaymentTypeLabel
                        paymentTypeId={paymentTypeIdWithFallback}
                      />
                    )}
                    {order.paymentCustomerBank && (
                      <div>
                        <BankLabel bankCode={order.paymentCustomerBank} />
                      </div>
                    )}
                    {order.paymentCustomerName && (
                      <div>{order.paymentCustomerName}</div>
                    )}
                    {order.paymentCustomerIBAN && (
                      <div>{order.paymentCustomerIBAN}</div>
                    )}
                    {order.paymentCustomerAccountNumber && (
                      <div>{order.paymentCustomerAccountNumber}</div>
                    )}
                    {order.paymentCustomerUtr && (
                      <div>{order.paymentCustomerUtr}</div>
                    )}
                  </Fragment>
                }
              />
            )}
            {orderUtils.isCustomerPaid(
              order,
              statusHistoryQueryResult.data,
            ) && (
              <DescriptionRow
                title={t('confirm_date')}
                value={formatUtils.formatDate(
                  orderUtils.getStatusDate(
                    order,
                    OrderStatus.Completed,
                    statusHistoryQueryResult.data,
                  ),
                )}
              />
            )}
            {isTraderOrCustomerConfirmStatus && isNSPK && nspkDetails && (
              <DescriptionRow
                title={t('nspk')}
                value={
                  <Fragment>
                    {nspkDetails?.operationNumber && (
                      <DescriptionRow
                        title={t('nspk_operation_number')}
                        value={
                          <Fragment>
                            <CopyText text={nspkDetails.operationNumber} />
                          </Fragment>
                        }
                      />
                    )}
                    {nspkDetails?.qrId && (
                      <DescriptionRow
                        title={t('nspk_qr_id')}
                        value={
                          <Fragment>
                            <CopyText text={nspkDetails.qrId} />
                          </Fragment>
                        }
                      />
                    )}
                  </Fragment>
                }
              />
            )}
            {hasReceipt && (
              <DescriptionRow
                title={t('receipts')}
                value={
                  <div>
                    <Link
                      component="button"
                      variant="body2"
                      underline="none"
                      onClick={handleReceiptsClick}
                    >
                      {tCommon('buttons.open')}
                    </Link>
                  </div>
                }
              />
            )}
            {isManager && hasReceipt && (
              <DescriptionRow
                title={t('validation')}
                value={<OrderReceiptsValidation order={order} labelled />}
              />
            )}
            {isManager && (
              <P2PProvidersRequests
                orderId={order.id}
                orderType={OrderType.Payin}
              />
            )}
            {isManager && (
              <MerchantRequests
                orderId={order.id}
                orderType={OrderType.Payin}
              />
            )}
            {isAdminOrTechOperator && (
              <OrderWebhooksLink
                orderId={order.id}
                orderType={OrderType.Payin}
                createdAt={order.createdAt}
              />
            )}
            {isManager && <SciEvents order={order} />}
            {order.p2pProviderOrder && (
              <P2PProviderOrderDetails order={order} />
            )}
            {(isManager || isTrader) && (
              <OrderStatusHistory
                order={order}
                loading={loading}
                statusHistoryQueryResult={statusHistoryQueryResult}
              />
            )}
            {isAdminOrTechOperator &&
              isCancelled &&
              !!order.customerCancelFeedback && (
                <DescriptionRow
                  title={tOrders('customer_cancel_feedback.label')}
                  value={
                    <Fragment>
                      {order.customerCancelFeedback.reasons.map((reason) => (
                        <div key={reason}>
                          {tOrders(
                            `customer_cancel_feedback.reasons.${
                              reason as CustomerCancelReason
                            }`,
                          )}
                        </div>
                      ))}
                      {order.customerCancelFeedback.comment && (
                        <div className="tw-break-words">
                          <div className="tw-mt-1 tw-text-secondary">{`${tOrders(
                            'customer_cancel_feedback.comment_label',
                          )}: `}</div>
                          {order.customerCancelFeedback.comment}
                        </div>
                      )}
                    </Fragment>
                  }
                />
              )}
          </Stack>
          {isTrader &&
            !isRequisitesVerification &&
            (isTraderOrCustomerConfirmStatus || isHoldCompleted) &&
            !orderUtils.isStatusTimeoutOver(order) && (
              <Stack direction="column" spacing={3}>
                {traderCanOpenDispute && (
                  <Button
                    variant="outlined"
                    color="error"
                    disabled={loading}
                    onClick={handleOpenDispute}
                    fullWidth
                  >
                    {tOrders('open_dispute')}
                  </Button>
                )}
                {!isHoldCompleted && (
                  <Button
                    variant="contained"
                    color="secondary"
                    disabled={loading}
                    onClick={handleConfirmClick}
                    fullWidth
                  >
                    {tCommon('buttons.confirm')}
                  </Button>
                )}
              </Stack>
            )}
          {isTrader && isCancelled && traderCanCompleteCancelled && (
            <ConfirmButton
              variant="outlined"
              color="secondary"
              disabled={loading}
              fullWidth
              onConfirm={onCompleteCancelled}
            >
              {tOrders('buttons.complete_cancelled')}
            </ConfirmButton>
          )}
          {isManager &&
            !isRequisitesVerification &&
            (isTraderOrCustomerConfirmStatus ||
              (isHoldCompleted && !orderUtils.isStatusTimeoutOver(order))) && (
              <Button
                variant="outlined"
                color="error"
                disabled={loading}
                onClick={handleOpenDispute}
                fullWidth
              >
                {tOrders('open_dispute')}
              </Button>
            )}
          {isManager && isCompleted && (
            <Stack direction="column" spacing={3}>
              {!order.p2pProviderOrderId && (
                <Button
                  variant="outlined"
                  disabled={loading}
                  fullWidth
                  onClick={() => setInternalStatusDetailsDialogOpen(true)}
                >
                  {tOrders('buttons.internal_status')}
                </Button>
              )}
              <ConfirmButton
                variant="outlined"
                color="error"
                disabled={loading}
                fullWidth
                onConfirm={onCancelCompleted}
              >
                {tCommon('buttons.cancel')}
              </ConfirmButton>
            </Stack>
          )}
          {isManager && isDispute && (
            <Stack direction="column" spacing={3}>
              {isDifferentAmountDispute && (
                <Button
                  variant="outlined"
                  disabled={loading}
                  fullWidth
                  onClick={() => setChangeDisputeAmountDialogOpen(true)}
                >
                  {tOrders('buttons.change_dispute_amount')}
                </Button>
              )}
              <Button
                variant="outlined"
                disabled={loading}
                fullWidth
                onClick={() => setInternalStatusDetailsDialogOpen(true)}
              >
                {tOrders('buttons.internal_status')}
              </Button>
              <Button
                variant="outlined"
                disabled={loading}
                fullWidth
                onClick={() => setResolveDisputeDialogOpen(true)}
              >
                {tOrders('buttons.resolve_dispute')}
              </Button>
            </Stack>
          )}
        </Stack>
      </DataWrapper>
      {hasReceipt && (
        <ReceiptsModal
          open={receiptsModalOpen}
          order={order}
          onClose={handleReceiptsModalClose}
        />
      )}
      <DisputeDialog
        open={disputeDialogOpen}
        order={order}
        title={tOrders('open_dispute')}
        onClose={handleDisputeDialogClose}
      />
      <Dialog
        title={tOrders('confirm_dialog.title')}
        onClose={handleConfirmDialogClose}
        open={confirmDialogOpen}
      >
        {tOrders('confirm_dialog.description')}
      </Dialog>
      <ResolveDisputeDialog
        open={resolveDisputeDialogOpen}
        order={order}
        error={resolveOrderDisputeAsAdminError}
        amountDisabled={isTrader}
        onClose={handleResolveDisputeDialogClose}
      ></ResolveDisputeDialog>
      <InternalStatusDetailsDialog
        open={internalStatusDetailsDialogOpen}
        order={order}
        onClose={handleInternalStatusDetailsDialogClose}
      />
      <ChangeDisputeAmountDialog
        open={changeDisputeAmountDialogOpen}
        order={order}
        error={changeDisputeAmountError}
        onClose={handleChangeDisputeAmountDialogClose}
      />
    </Fragment>
  );
};
